import {useEffect, useMemo, useRef, useState} from "react";
import "chartjs-adapter-date-fns";
import Chart from "chart.js/auto";
import  {it}  from 'date-fns/locale';
import { format, isValid, parseISO, setDefaultOptions, subDays } from 'date-fns';

import { TableRow, Typography, useMediaQuery } from "@mui/material";
import { transparentize } from "polished";

import { BoxSquareColor, PaperChartCustom } from "../../../../Theme/ContainerStyle";
import { TooltipChartWrapper } from "../../../../components/Wrapper";
import { formatColumns } from "../../../../lib/Utility";
import { tooltipPositionPlugin } from "./PluginBar";

setDefaultOptions({ locale: it });

  ////SCALES SERVE PER DEFINIRE LE OPZIONI DEGLI ASSI
  ////CHARTOPTIONS PREIMPOSTA IL GRAFICO
  
export default function ChartBar(props) {

  const {datasets, dateRange, titleVaribles, activeParagon, periodDayPrevious} = props

  const chartOptions = {
    scales: {
      x: {
        stacked: true,
        type: "timeseries",
        time: {
          unit: 'day', //forzo il formato in giorni
          displayFormats: {day: "dd MMM"},
          tooltipFormat:"dd/MM/yyyy",     
        },
        ticks: {source: "labels"},
      },   
      y: {stacked: true} //stacked: risultati inpilati
    },
    plugins: {
      legend: {display: false}, //configurazione del tooltip
      tooltip: {
        mode: "index",
        position: "custom",
        intersect: false,
        enabled: false,
        callbacks: {
          title(tooltipItems) {
            return "Periodo: " + tooltipItems[0].label;
          }
        },
      },
    },
  };
  

  const currentDataset = (datasets.find(data => data?.name === "currentData")?.dataset) || [];
  const previousDataset = (datasets.find(data => data?.name === "dataPrevious")?.dataset) || [];

  const canvas = useRef(null);
  const chart = useRef(null);
  const [tooltip, setTooltip] = useState({show: false});
  const sizeMedia = useMediaQuery('(min-width:800px)');
  const [itemHover, setItemHover] = useState(null)

  const onHoverHandler = (event, elements) => {
    // Trova l'indice del dataset corrente
    const currentDatasetIndex = elements.length > 0 ? elements[0].datasetIndex - (activeParagon ? previousDataset.length : 0) : null;
    setItemHover(currentDatasetIndex);
  };

  /// SIZE FA SI CHE IL GRAFICO SI ADATTI IN BASE ALLA MAPPA SE è APERTA O CHIUSA
  const size = useMemo(() => {
    return sizeMedia
      ? { maintainAspectRatio: true, aspectRatio: 3 }
      : { maintainAspectRatio: false, aspectRatio: 1 };
  }, [sizeMedia]);

  const columns = useMemo(() => {
    const body = tooltip.body || []; // Assicurati che body sia un array
  
    // Trova le etichette uniche
    const uniqueLabels = new Set();
    body.forEach((lines) => {
      const [label] = lines[0].split(":");
      const cleanedLabel = label.split('-')[0].trim();
      uniqueLabels.add(cleanedLabel);
    });
  
    // Calcola il numero di colonne basato sulle etichette uniche
    const numColumns = formatColumns(Array.from(uniqueLabels));
    const combinedData = {}; // Oggetto per raccogliere i dati combinati
  
    // Itera attraverso i dati del tooltip
    body.forEach((lines, index) => {
      const [label, value] = lines[0].split(":");
      const cleanedLabel = label.split('-')[0].trim();
      const color = activeParagon ? currentDataset[index % currentDataset.length].borderColor : tooltip.colors[index].borderColor;
  
      // Se l'etichetta esiste già, aggiungi il valore
      if (combinedData[cleanedLabel]) {
        combinedData[cleanedLabel].value.push(value.trim());
      } else {
        // Altrimenti, crea una nuova voce
        combinedData[cleanedLabel] = {
          label: cleanedLabel,
          value: [value.trim()],
          color: color,
          id: index
        };
      }
    });
  
    // Converti l'oggetto in un array
    const combinedArray = Object.values(combinedData);
    
    // Riorganizza i dati in colonne
    return combinedArray.reduce((acc, item, index) => {
      acc[index % numColumns].push(item);
      return acc;
    }, new Array(numColumns).fill(0).map(() => []));
  }, [tooltip.body, tooltip.colors]);

  // Allineamento delle date per posizionare sulla stessa data anche quella antecedente
  // periodDayPrevious è passato dal liv superiore per andare a a settare il periodo
  const alignDatasets = () => {
    const alignedDataPrevious = previousDataset.map((data, index) => ({
      label: data.label,
      data: dateRange.map(date => {
        const alignedDate = subDays(parseISO(date), periodDayPrevious);
        if (!isValid(alignedDate)) {
          return { x: date, y: 0 };
        }
        const alignedValue = data.data.find((d) => {
          const parsedDate = parseISO(d.x);
          return isValid(parsedDate) && format(parsedDate, 'yyyy-MM-dd') === format(alignedDate, 'yyyy-MM-dd');
        });
        return alignedValue ? { x: date, y: alignedValue.y } : { x: date, y: 0 };
      }),
      backgroundColor: transparentize(0.5,data.backgroundColor), 
      borderColor: transparentize(0.5,data.borderColor),
      stack: 'stack0',
      borderWidth:0,
    }));

    return alignedDataPrevious;
  }
        ///INIZIALIZZAZIONE E AGGIORNAMENTO GRAFICO, SE ESISTE VIENE SEMPLICEMENTE AGGIORNATO
  ///ALTRIMENTI NE CREA UNO NUOVO
  useEffect(() => {
    const alignedDataPrevious = alignDatasets();
    const combinedDatasets = activeParagon ? [
      ...alignedDataPrevious,
      ...currentDataset.map(data => ({
        ...data,
        borderWidth:0,
        stack: 'stack1',
        categoryPercentage: 0.9, // Riduce lo spazio tra i gruppi di barre
        barPercentage:1.1, // Riduce la larghezza delle barre all'interno di un gruppo    
      })),
    ] : currentDataset

    if (chart.current) {
      chart.current.data.labels = dateRange;
      chart.current.data.datasets = combinedDatasets;
      chart.current.options.scales = chartOptions.scales;
      chart.current.update();
    } else {    
      chart.current = new Chart(canvas.current,
        {
          type: "bar",
          data: {labels: dateRange,datasets: combinedDatasets},
          options: {
            ...chartOptions,
            ...size,
            onHover:onHoverHandler,
            plugins: {
              ...chartOptions.plugins,
              tooltip: {
                ...chartOptions.plugins.tooltip,
                external(context) {
                  // Hide if no tooltip
                  const tooltipModel = context.tooltip;
                  if (tooltipModel.opacity === 0) {
                    setTooltip({
                      show: false,
                    });
                    return;
                  }

                  const {offsetLeft: positionX, offsetTop: positionY} = context.chart.canvas;

                  setTooltip({
                    show: true,
                    top: positionY + tooltipModel.caretY + 'px',
                    left: positionX + tooltipModel.caretX + 'px',
                    title: tooltipModel.title,
                    body: tooltipModel.body.map(bodyItem => bodyItem.lines),
                    colors: tooltipModel.labelColors,
                  });
                }
              }
            }
          }
        }
      );
    }

    ///PULIZIA E DISTRUZIONE GRAFICO QUANDO IL COMPONENTE VIENE SMONTATO
    ///EVITA PERDITE DI MEMORIA
    return () => {
      if (chart.current) {
        chart.current.destroy();
        chart.current = null;
      }
    };
  // eslint-disable-next-line
  }, [dateRange, currentDataset, size, activeParagon,periodDayPrevious]);
  
  ///FUNZIONE CALCOLO PERCENTUALE
  const calculatePerc = (oldValue, newValue) => {
    if (oldValue === 0) {
      return ((newValue - (oldValue)) * 100);
    }
    return (((newValue - (oldValue)) / (oldValue)) * 100);
  }
  
  ///RENDER DEL CONTENUTO DEL TOOLTIP
  const renderItem = (props) => {
    const {label, value, color, id, index} = props
    const hoverElement = itemHover === id
    const styleFontSize = hoverElement ? "14px" : "12px"
    const styleBold = hoverElement ? "bold" : ""
    const styleSize = hoverElement ? 20 : null
    const styleFontSize1 = sizeMedia ? styleFontSize : "8px"
    const valueNum = value.map(val => parseFloat(val));
    const percentageChange = calculatePerc(parseFloat(value[0]), parseFloat(value[1])).toFixed(2); // Calcola la variazione percentuale
    const colorValue = percentageChange && percentageChange > 0 ? "red" : "green"

    return (
      <TableRow key={index}>
        <td>
          <Typography style={{fontSize:styleFontSize1, fontWeight:styleBold, whiteSpace: "nowrap"}}>
            <BoxSquareColor color = {color} size={styleSize}/> 
            {hoverElement ? <strong>{label}</strong> : label}
          </Typography>
        </td>
        <td> 
          <Typography style={{fontWeight:"bold",fontSize:styleFontSize1, textAlign: "right", whiteSpace: "nowrap"}}>
            {activeParagon ?  
              <>
                <span>{valueNum[1]}</span>
                {/* <span>{" - " + valueNum[0]}</span> */}
                <span style={{color: colorValue}}>{" (" + percentageChange + "%)"}</span> 
              </> : 
              value}
            {sizeMedia && <span>{" - Kwh"}</span>}
          </Typography>
        </td>
      </TableRow>  
    )
  }

  return (
    <>
      <PaperChartCustom className={"chart-area"} >
        <canvas ref={canvas}/>
      </PaperChartCustom>
      <TooltipChartWrapper
          widthTooltip={sizeMedia ? 300 : 180}
          tooltip={tooltip}
          columns={columns}
          titleTooltip={tooltip.title && tooltip.title[0] + "\n " + titleVaribles}
          plugin={() => tooltipPositionPlugin({activeParagon, sizeMedia})}
          renderItem={({label, value, color, id}, index) => 
            renderItem({ label, value, color, id }, index)}
      />
    </>
  )
}
